import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Injector,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { takeUntil, catchError, throwError } from 'rxjs';
import { TuiDialogService } from '@taiga-ui/core';
import { PolymorpheusComponent } from '@taiga-ui/polymorpheus';
import { TranslateService } from '@ngx-translate/core';
import { DialogConfirmComponent } from '@src/app/shared/dialogs';
import { AlertService } from '@src/core/services';
import { ViewMode } from '@src/models';
import { BaseComponent } from '@src/app/modules/base';

import { LoyaltyProgramPermService } from '../../services';
import { LpProductRequestService } from '../services/lp-product-request.service';
import { ProductRequestType } from '../types';

import { SuccessfulSaveDialogComponent } from './dialogs';

@Component({
  selector: 'lp-product-request-info',
  templateUrl: './lp-product-request-info.component.html',
  styleUrls: ['./lp-product-request-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LpProductRequestInfoComponent extends BaseComponent implements OnChanges {
  private readonly lpProductRequestService = inject(LpProductRequestService);
  private readonly permService = inject(LoyaltyProgramPermService);
  private readonly translateService = inject(TranslateService);
  private readonly alertService = inject(AlertService);
  private readonly dialogService = inject(TuiDialogService);
  private readonly injector = inject(Injector);

  @Input() mode: ViewMode = 'view';
  @Input() id?: string;

  @Output() cancelEditing = new EventEmitter<void>();
  @Output() saved = new EventEmitter<void>();
  @Output() deleted = new EventEmitter<void>();
  @Output() onClickEditButton = new EventEmitter<void>();

  data?: ProductRequestType;

  allowEditing$ = this.permService.allowProductRequestEditing$;
  allowDeleting$ = this.permService.allowProductRequestDeleting$;

  loading = false;

  private readonly confirmDeleteDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('common.dialogs.deleteHeader'),
      size: 's',
      closeable: false,
    },
  );

  private readonly confirmCancelEditDialog = this.dialogService.open<boolean>(
    new PolymorpheusComponent(DialogConfirmComponent, this.injector),
    {
      label: this.translateService.instant('common.dialogs.cancelHeader'),
      size: 's',
      closeable: false,
    },
  );

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.id) {
      if (this.id && this.data?.id !== this.id) {
        this.loadProductRequestData(this.id);
      }
    }

    if (changes.mode) {
      if (this.mode === 'create') {
        this.data = undefined;
        this.cdr.markForCheck();
      }
    }
  }

  onClickDeleteButton() {
    this.confirmDeleteDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: res => {
        const collectionId = this.data?.id;

        if (res && collectionId) {
          this.lpProductRequestService
            .deleteProductRequest(collectionId)
            .pipe(takeUntil(this.destroyed$$))
            .subscribe(() => {
              this.alertService.success(
                this.translateService.instant('components.lpProductRequestInfo.alerts.successes.deleteProductRequest'),
              );
              this.deleted.emit();
            });
        }
      },
    });
  }

  async onClickSaveButton(productRequest: ProductRequestType) {
    this.loading = true;

    if (this.mode === 'create') {
      this.lpProductRequestService
        .createProductRequest(productRequest)
        .pipe(
          catchError(err => {
            this.loading = false;
            this.cdr.markForCheck();

            return throwError(() => err);
          }),
          takeUntil(this.destroyed$$),
        )
        .subscribe(res => {
          if (res.id) {
            this.dialogService
              .open(new PolymorpheusComponent(SuccessfulSaveDialogComponent, this.injector), {
                size: 'l',
                closeable: false,
                data: productRequest.name,
              })
              .pipe(takeUntil(this.destroyed$$))
              .subscribe(() => {
                this.saved.emit();

                this.loading = false;
                this.cdr.markForCheck();
              });
          } else {
            this.loading = false;
            this.cdr.markForCheck();
          }
        });
    }

    if (this.mode === 'edit' && productRequest.newComment) {
      this.lpProductRequestService
        .editProductRequest(productRequest.newComment)
        .pipe(
          catchError(err => {
            this.loading = false;
            this.cdr.markForCheck();

            return throwError(() => err);
          }),
          takeUntil(this.destroyed$$),
        )
        .subscribe(res => {
          if (res.id) {
            this.loadProductRequestData(res.id);
            this.saved.emit();
          }

          this.loading = false;
          this.cdr.markForCheck();
        });
    }
  }

  onClickCancelButton() {
    this.confirmCancelEditDialog.pipe(takeUntil(this.destroyed$$)).subscribe({
      next: res => {
        if (res) {
          this.cancelEditing.emit();
        }
      },
    });
  }

  private loadProductRequestData(id: string) {
    this.loading = true;

    this.lpProductRequestService
      .getProductRequestById(id)
      .pipe(
        catchError(err => {
          this.data = undefined;
          this.loading = false;
          this.cdr.markForCheck();

          return throwError(() => err);
        }),
        takeUntil(this.destroyed$$),
      )
      .subscribe(data => {
        this.data = data;

        this.permService.loadProductRequestPermissions(data.userId);

        this.loading = false;
        this.cdr.markForCheck();
      });
  }
}
